// Spec -> styled exceljs workbook translation for the Set Summary export.
// exceljs is injected (never imported here) so `node --test` can exercise the
// full workbook build with the node build of exceljs, while the browser runner
// passes the lazily-imported bundle. Same injection pattern as
// collectionExport.mjs.
import { PALETTE } from './setSummaryExport.mjs';

// exceljs uses ARGB; specs carry '#RRGGBB'.
const argb = (hex) => 'FF' + String(hex).replace('#', '').toUpperCase();

// Excel has no Orbitron/Rajdhani — Verdana is the closest wide geometric
// stand-in that ships everywhere; Consolas for card keys.
const DISPLAY_FONT = 'Verdana';
const BODY_FONT = 'Calibri';
const MONO_FONT = 'Consolas';

const thin = { style: 'thin', color: { argb: argb(PALETTE.border) } };
const boxBorder = { top: thin, left: thin, bottom: thin, right: thin };
const fill = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: argb(hex) } });
const indented = { vertical: 'middle', horizontal: 'left', indent: 1 };

// Translate one neutral sheet spec into a styled exceljs worksheet.
// Exported so the node smoke test can build a real workbook without a browser.
export function specToWorkbook(ExcelJS, spec) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'CollectionKeeper.org';

  for (const sheet of spec.sheets) {
    const ws = wb.addWorksheet(sheet.name, {
      properties: { tabColor: { argb: argb(sheet.tabColor) } },
    });
    ws.columns = sheet.columns.map((w) => ({ width: w }));

    for (const row of sheet.rows) {
      switch (row.kind) {
        case 'banner': {
          const r = ws.addRow([row.text]);
          ws.mergeCells(r.number, 1, r.number, row.cols);
          r.height = 30;
          r.getCell(1).style = {
            font: { name: DISPLAY_FONT, size: 14, bold: true, color: { argb: argb(PALETTE.gold) } },
            fill: fill(PALETTE.ink),
            alignment: indented,
          };
          if (row.sub) {
            const r2 = ws.addRow([row.sub]);
            ws.mergeCells(r2.number, 1, r2.number, row.cols);
            r2.height = 18;
            r2.getCell(1).style = {
              font: { name: BODY_FONT, size: 10, color: { argb: argb(PALETTE.cyan) } },
              fill: fill(PALETTE.ink),
              alignment: indented,
            };
          }
          break;
        }
        case 'lede': {
          const r = ws.addRow([row.text]);
          ws.mergeCells(r.number, 1, r.number, row.cols);
          r.height = 58;
          r.getCell(1).style = {
            font: { name: BODY_FONT, size: 10, italic: true, color: { argb: 'FF3A4150' } },
            alignment: { vertical: 'top', horizontal: 'left', wrapText: true, indent: 1 },
          };
          break;
        }
        case 'section': {
          ws.addRow([]);
          const r = ws.addRow([row.text]);
          ws.mergeCells(r.number, 1, r.number, row.cols);
          r.height = 20;
          r.getCell(1).style = {
            font: { name: DISPLAY_FONT, size: 10, bold: true, color: { argb: argb(row.accent) } },
            fill: fill(PALETTE.panel),
            alignment: indented,
          };
          break;
        }
        case 'header': {
          const r = ws.addRow(row.cells);
          r.height = 20;
          r.eachCell({ includeEmpty: true }, (cell) => {
            cell.style = {
              font: { name: DISPLAY_FONT, size: 9, bold: true, color: { argb: 'FFFFFFFF' } },
              fill: fill(PALETTE.headFill),
              alignment: indented,
              border: boxBorder,
            };
          });
          if (row.freeze) ws.views = [{ state: 'frozen', ySplit: r.number }];
          if (row.filter) {
            ws.autoFilter = {
              from: { row: r.number, column: 1 },
              to: { row: r.number, column: row.cells.length },
            };
          }
          break;
        }
        case 'data': {
          const r = ws.addRow(row.cells);
          r.eachCell({ includeEmpty: true }, (cell, col) => {
            const mono = row.mono?.includes(col);
            const big = row.big?.includes(col);
            cell.style = {
              font: {
                name: mono ? MONO_FONT : big ? DISPLAY_FONT : BODY_FONT,
                size: big ? 11 : 10,
                bold: !!big,
                color: { argb: argb(PALETTE.text) },
              },
              fill: row.stripe ? fill(PALETTE.stripe) : undefined,
              alignment: indented,
              border: boxBorder,
            };
          });
          if (row.swatch) r.getCell(1).fill = fill(row.swatch);
          break;
        }
        case 'note': {
          const r = ws.addRow([row.text]);
          ws.mergeCells(r.number, 1, r.number, row.cols);
          r.getCell(1).style = {
            font: { name: BODY_FONT, size: 8, color: { argb: argb(PALETTE.muted) } },
            alignment: indented,
          };
          break;
        }
        case 'blank':
        default:
          ws.addRow([]);
      }
    }
  }
  return wb;
}

